/**
 * Admin Ride-Health panel — the operational control room's nervous system.
 *
 * Polls `GET /api/admin/ride-health` every 15s and surfaces six cards:
 *   1. 24h summary headline (chips)
 *   2. Stuck rides (currently flagged + still non-terminal)  → action buttons
 *   3. Auto recoveries (orphan-offer rescues by the janitor, last 24h)
 *   4. Driver inactivity (auto-offlined drivers, last 24h)
 *   5. Disconnect storms (WS-disconnect clusters of 2+ in last 24h)
 *   6. Currently offered past TTL (pre-rescue early-warning list)
 *
 * Pure presentation — every mutation (re-dispatch / force-cancel) is
 * delegated upward via props so the orchestrator's optimistic-update
 * + toast wiring stays in one place.
 */
import React from "react";
import { AlertTriangle, RefreshCcw, UserX, Wifi, Clock, Activity } from "lucide-react";

const formatRelative = (iso) => {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (!ts) return iso;
  const secs = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const short = (id) => (id ? String(id).slice(0, 8) : "—");

/* -------------------- Summary headline chips -------------------- */

const SummaryChips = ({ summary }) => {
  if (!summary) return null;
  const cards = [
    { k: "rides_completed", l: "COMPLETED", c: "#10B981" },
    { k: "rides_cancelled", l: "CANCELLED", c: "#9CA3AF" },
    { k: "orphan_offers_recovered", l: "ORPHAN RECOVERIES", c: "#002FA7" },
    { k: "rides_flagged_stuck", l: "STUCK RIDES", c: "#EF4444" },
    { k: "drivers_auto_offlined", l: "AUTO-OFFLINED", c: "#F97316" },
    { k: "ws_disconnect_clusters", l: "DISCONNECT STORMS", c: "#7C3AED" },
    { k: "ws_disconnects", l: "WS DROPS", c: "#6B7280" },
    { k: "transition_rejections", l: "REJECTIONS", c: "#0A0A0A" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border" data-testid="ride-health-summary-24h">
      {cards.map((c) => (
        <div key={c.k} className="bg-white px-4 py-3" data-testid={`summary-${c.k}`}>
          <div className="label-mini text-muted-foreground text-[10px] mb-1" style={{ color: c.c }}>
            {c.l}
          </div>
          <div className="font-heading text-2xl font-black tracking-tight">{summary[c.k] ?? 0}</div>
        </div>
      ))}
    </div>
  );
};

/* -------------------- Section header -------------------- */

const SectionHeader = ({ icon: Icon, title, count, accent, subtitle }) => (
  <div className="flex items-start justify-between border-b border-border px-4 py-3 bg-secondary/20">
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4" strokeWidth={2.5} style={{ color: accent || "#0A0A0A" }} />
      <h3 className="font-heading text-sm font-bold tracking-wide uppercase">{title}</h3>
      <span className="bg-[#0A0A0A] text-white px-2 py-0.5 rounded-sm text-[10px] font-bold">
        {count}
      </span>
    </div>
    {subtitle && <span className="label-mini text-muted-foreground text-[10px]">{subtitle}</span>}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="px-4 py-6 text-center text-sm text-muted-foreground italic">{message}</div>
);

/* -------------------- Stuck rides table -------------------- */

const StuckRidesCard = ({ rides, onRedispatch, onForceCancel, onForceComplete, onResolve }) => (
  <div className="border border-border rounded-sm bg-white overflow-hidden" data-testid="ride-health-stuck-card">
    <SectionHeader
      icon={AlertTriangle}
      title="Stuck rides"
      count={rides.length}
      accent="#EF4444"
      subtitle="non-terminal · flagged by janitor"
    />
    {rides.length === 0 ? (
      <EmptyState message="No stuck rides right now. The system is healthy." />
    ) : (
      <div className="divide-y divide-border">
        {rides.map((r) => {
          // Only in_progress rides can be force-completed (server enforces).
          // For these the COMPLETE action is the revenue-preserving choice;
          // CANCEL is the no-charge choice. Both stay visible so the
          // operator picks deliberately.
          const completable = r.status === "in_progress";
          return (
          <div key={r.id} className="px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`stuck-row-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-xs label-mini">
                <span className="font-bold text-foreground">#{short(r.id)}</span>
                <span className="px-1.5 py-0.5 bg-[#FEE2E2] text-[#991B1B] rounded-sm">{r.phase}</span>
                <span className="text-muted-foreground">flagged {formatRelative(r.stuck_logged_at)}</span>
              </div>
              <div className="mt-1 text-sm font-medium truncate">{r.passenger_name || "—"}</div>
              <div className="text-xs text-muted-foreground truncate">
                {r.pickup_address || "—"} → {r.dropoff_address || "—"}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <button
                onClick={() => onResolve(r.id)}
                className="px-3 py-1.5 text-xs font-bold bg-white border border-border text-foreground rounded-sm hover:bg-secondary transition-colors"
                data-testid={`stuck-resolve-${r.id}`}
                title="Acknowledge — clear the stuck flag without changing the ride state"
              >
                RESOLVE
              </button>
              <button
                onClick={() => onRedispatch(r.id)}
                className="px-3 py-1.5 text-xs font-bold bg-[#002FA7] text-white rounded-sm hover:bg-[#001F70] transition-colors"
                data-testid={`stuck-redispatch-${r.id}`}
              >
                RE-DISPATCH
              </button>
              {completable && (
                <button
                  onClick={() => onForceComplete(r.id)}
                  className="px-3 py-1.5 text-xs font-bold bg-[#16A34A] text-white rounded-sm hover:bg-[#15803D] transition-colors"
                  data-testid={`stuck-complete-${r.id}`}
                  title="Force-complete — passenger travelled, GPS/geofence prevented driver completion. Preserves fare and activates payment."
                >
                  FORCE COMPLETE
                </button>
              )}
              <button
                onClick={() => onForceCancel(r.id)}
                className="px-3 py-1.5 text-xs font-bold bg-[#EF4444] text-white rounded-sm hover:bg-[#B91C1C] transition-colors"
                data-testid={`stuck-cancel-${r.id}`}
                title="Force-cancel — no payment, no fare. Use only if the journey should not be charged."
              >
                FORCE CANCEL
              </button>
            </div>
          </div>
          );
        })}
      </div>
    )}
  </div>
);

/* -------------------- Auto recoveries list -------------------- */

const AutoRecoveriesCard = ({ recoveries }) => (
  <div className="border border-border rounded-sm bg-white overflow-hidden" data-testid="ride-health-recoveries-card">
    <SectionHeader
      icon={RefreshCcw}
      title="Auto recoveries"
      count={recoveries.length}
      accent="#002FA7"
      subtitle="orphan offers rescued · 24h"
    />
    {recoveries.length === 0 ? (
      <EmptyState message="No orphan-offer recoveries in the last 24h." />
    ) : (
      <div className="divide-y divide-border max-h-80 overflow-y-auto">
        {recoveries.map((e, i) => (
          <div key={`${e.ride_id}-${i}`} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`recovery-row-${e.ride_id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold">#{short(e.ride_id)}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                offered → dispatching · prev driver {short(e.metadata?.previous_offered_to)}
              </div>
            </div>
            <div className="text-xs text-muted-foreground whitespace-nowrap">{formatRelative(e.ts)}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* -------------------- Driver inactivity list -------------------- */

const DriverInactivityCard = ({ drivers }) => (
  <div className="border border-border rounded-sm bg-white overflow-hidden" data-testid="ride-health-inactivity-card">
    <SectionHeader
      icon={UserX}
      title="Driver inactivity"
      count={drivers.length}
      accent="#F97316"
      subtitle="auto-offlined · 24h"
    />
    {drivers.length === 0 ? (
      <EmptyState message="No drivers were auto-offlined in the last 24h." />
    ) : (
      <div className="divide-y divide-border max-h-80 overflow-y-auto">
        {drivers.map((d) => (
          <div key={d.id} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`inactivity-row-${d.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold truncate">{d.name || d.email || short(d.id)}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {d.auto_offlined_reason || "inactivity_timeout"} · last_seen {formatRelative(d.last_seen)}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span
                className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${
                  d.is_online ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#F3F4F6] text-[#374151]"
                }`}
              >
                {d.is_online ? "BACK ONLINE" : "OFFLINE"}
              </span>
              <span className="text-[10px] text-muted-foreground">{formatRelative(d.auto_offlined_at)}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* -------------------- Disconnect storms (clusters) -------------------- */

const DisconnectStormsCard = ({ clusters }) => (
  <div className="border border-border rounded-sm bg-white overflow-hidden" data-testid="ride-health-storms-card">
    <SectionHeader
      icon={Wifi}
      title="Disconnect storms"
      count={clusters.length}
      accent="#7C3AED"
      subtitle="2+ drops same user · 24h"
    />
    {clusters.length === 0 ? (
      <EmptyState message="No disconnect clusters detected. Everyone's connection is stable." />
    ) : (
      <div className="divide-y divide-border max-h-80 overflow-y-auto">
        {clusters.map((c, i) => (
          <div key={`${c.actor}-${i}`} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`storm-row-${c.user_id || c.actor}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-bold">{c.name || c.email || short(c.user_id) || c.actor}</span>
                <span
                  className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase"
                  style={{
                    backgroundColor: c.role === "driver" ? "#EDE9FE" : "#FEF3C7",
                    color: c.role === "driver" ? "#5B21B6" : "#92400E",
                  }}
                >
                  {c.role}
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                {c.disconnect_count} drops · {c.distinct_rides} rides · last {formatRelative(c.last_ts)}
              </div>
            </div>
            <div className="font-heading text-xl font-black text-[#7C3AED]">×{c.disconnect_count}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* -------------------- Offered-past-TTL early warning -------------------- */

const OfferedPastTtlCard = ({ rides, thresholdSeconds }) => (
  <div className="border border-border rounded-sm bg-white overflow-hidden" data-testid="ride-health-past-ttl-card">
    <SectionHeader
      icon={Clock}
      title="Offered past TTL"
      count={rides.length}
      accent="#F59E0B"
      subtitle={`> ${Math.round((thresholdSeconds || 180) / 60)}m · janitor will rescue`}
    />
    {rides.length === 0 ? (
      <EmptyState message="No offers are past TTL. Crystal Dispatch is keeping up." />
    ) : (
      <div className="divide-y divide-border max-h-60 overflow-y-auto">
        {rides.map((r) => (
          <div key={r.id} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`past-ttl-row-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold">#{short(r.id)} · {r.passenger_name || "—"}</div>
              <div className="text-[11px] text-muted-foreground truncate">{r.pickup_address || "—"}</div>
            </div>
            <div className="text-[11px] text-muted-foreground whitespace-nowrap">
              offered to {short(r.offered_to)} · {formatRelative(r.offered_at)}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* -------------------- Recent stuck-flag events (audit timeline) -------------------- */

const RecentStuckRidesCard = ({ events }) => (
  <div className="border border-border rounded-sm bg-white overflow-hidden" data-testid="ride-health-recent-stuck-card">
    <SectionHeader
      icon={Activity}
      title="Recent stuck-ride flags"
      count={events.length}
      accent="#DC2626"
      subtitle="includes resolved · 24h"
    />
    {events.length === 0 ? (
      <EmptyState message="No new stuck-ride flags in the last 24h." />
    ) : (
      <div className="divide-y divide-border max-h-60 overflow-y-auto">
        {events.map((e, i) => (
          <div key={`${e.ride_id}-${i}`} className="px-4 py-2 flex items-center justify-between gap-3 text-xs" data-testid={`recent-stuck-row-${e.ride_id}`}>
            <div className="min-w-0 flex-1 truncate">
              <span className="font-bold">#{short(e.ride_id)}</span>{" "}
              <span className="text-muted-foreground">flagged at phase</span>{" "}
              <span className="font-medium">{e.from_phase}</span>
            </div>
            <span className="text-muted-foreground whitespace-nowrap">{formatRelative(e.ts)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* -------------------- Main panel -------------------- */

export const RideHealthPanel = ({ data, lastFetchedAt, onRedispatch, onForceCancel, onForceComplete, onResolve }) => {
  if (!data) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="ride-health-loading">
        Loading ride-health…
      </div>
    );
  }

  return (
    <div className="p-3 md:p-4 space-y-4" data-testid="ride-health-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-black tracking-tight">RIDE HEALTH</h2>
        <span className="label-mini text-muted-foreground text-[10px]" data-testid="ride-health-fetched-at">
          updated {formatRelative(lastFetchedAt?.toISOString?.() || lastFetchedAt)}
        </span>
      </div>

      <SummaryChips summary={data.summary_24h} />

      <StuckRidesCard
        rides={data.currently_stuck_rides || []}
        onRedispatch={onRedispatch}
        onForceCancel={onForceCancel}
        onForceComplete={onForceComplete}
        onResolve={onResolve}
      />

      <div className="grid md:grid-cols-2 gap-4">
        <AutoRecoveriesCard recoveries={data.recent_orphan_recoveries_24h || []} />
        <DriverInactivityCard drivers={data.auto_offlined_drivers || []} />
      </div>

      <DisconnectStormsCard clusters={data.ws_disconnect_clusters_24h || []} />

      <div className="grid md:grid-cols-2 gap-4">
        <OfferedPastTtlCard
          rides={data.currently_offered_past_ttl || []}
          thresholdSeconds={data.windows?.offered_orphan_threshold_seconds}
        />
        <RecentStuckRidesCard events={data.recent_stuck_rides_24h || []} />
      </div>
    </div>
  );
};
